/* eslint-disable @typescript-eslint/no-explicit-any */
import type { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { getCountryStats } from 'src/actions/countryActions';
import { searchForAllCountriesStaticProps } from 'src/actions/searchActions';
import { NavFooter } from 'src/components/nav/navFooter.component';
import { worldDomainName } from 'src/config';
import type { OptionTypes } from 'src/types/actionSheetTypes';
import type { DataWorldChartTypes } from 'src/types/types';
import { dotsOfHistory } from 'src/utils/dots';
import getPassedSeconds from 'src/utils/getPassedSeconds';
import { getWorldPopulation } from 'src/utils/getWorldPopulation';
import StandardSEOWrapper from '../components/layout/standardSEOWrapper.component';
import { useRecordedPopulation } from '../hooks/useRecordedPopulation';
import type { WorldDataTypes } from '../types/world';
import { cn } from '../utils/cn';

export default function WhatIsThePopulationProject({
  countries,
}: Readonly<{
  countries: DataWorldChartTypes[];
}>) {
  const { t } = useTranslation(['what-is-the-population-project']);
  const [WORdata, setWORData] = useState<WorldDataTypes>();

  const recordedPopulation = useRecordedPopulation({
    lastRun: WORdata?.last_run,
    worldLivingPopulation: WORdata?.living,
    worldPendingPopulation: WORdata?.pending,
  });

  const [fillForReadMoreArrow, setFillForReadMoreArrow] =
    useState<string>('#409F85');

  const handleReadMoreArrow = () => {
    setFillForReadMoreArrow(
      fillForReadMoreArrow === '#409F85' ? '#ffffff' : '#409F85'
    );
  };

  useEffect(() => {
    (async () => {
      const data = await getCountryStats('WOR', worldDomainName);
      setWORData(data);
    })();
  }, []);

  const [populationCount, setPopulationCount] = useState(0);

  const completionBarRef = useRef<any>(null);

  const worldPopulation = getWorldPopulation();

  const [intPercentage, setIntPercentage] = useState<string>('0');

  const [barWidth, setBarWidth] = useState(0);

  useEffect(() => {
    const living = WORdata?.living || 0;
    const pending = WORdata?.pending || 0;
    let incrementValue =
      WORdata?.pending == 0 ? 0.2 : Math.round(pending / 86400);
    incrementValue = incrementValue > 10 ? 10 : incrementValue;
    const population = Math.round(
      living + getPassedSeconds(WORdata?.last_run) * incrementValue
    );

    setPopulationCount(population);

    setIntPercentage(((population / worldPopulation) * 100).toFixed(2));
    setBarWidth(
      parseFloat(((population / worldPopulation) * 100).toFixed(2)) *
        ((completionBarRef?.current?.clientWidth as number) / 100)
    );
  }, [WORdata, worldPopulation]);

  const [earthMarginRight, setEarthMarginRight] = useState<null | number>(null);

  const earthImageContainerRef = useRef<any>();

  const [option, setOption] = useState<OptionTypes>('');

  const handleOption = (option: OptionTypes) => () => {
    setOption(option);
  };

  return (
    <div className="flex flex-col max-md:pb-[120px]">
      <StandardSEOWrapper
        title={t('what-is-the-population-project:seo.title')}
        description={t('what-is-the-population-project:seo.description')}
      />
      <div className={`relative flex flex-col`}>
        <Image
          priority
          src="/images/what-image.png"
          layout="responsive"
          width={700}
          height={375}
          alt="People"
        />
        <div
          style={{ boxShadow: '19px 23px 72px -9px rgba(0, 0, 0, 0.2)' }}
          className={cn(
            'flex flex-col relative bg-white',
            'mx-[25px] md:mx-[30px] xl:mx-[113px]',
            'pt-[30px] md:pt-[40px] xl:pt-[50px]',
            'pb-[35px] md:pb-[45px] xl:pb-[55px]',
            '-mt-[80px] md:-mt-[120px] xl:-mt-[200px]',
            'px-[35px] md:px-[35px] xl:px-[55px]'
          )}
        >
          <p
            style={{ lineHeight: '115%', letterSpacing: '0.03em' }}
            className="text-[27px] font-semibold md:text-[40px] xl:w-[90%] xl:text-[80px]"
          >
            WHAT IS THE POPULATION PROJECT?
          </p>
          <div className="mt-[30px] h-[2.5px] w-[50%] bg-[#409F85] md:h-[5px]" />
        </div>
      </div>
      <div className="mx-[25px] mb-[99px] mt-[39px] flex max-xl:flex-col md:mx-[50px] md:mb-[167px] md:mt-[93px] xl:mx-[113px] xl:mb-[191px] xl:mt-[106px]">
        <div className={`mr-[187px] max-xl:w-full`}>
          <p className={`text-[25px] font-bold md:text-[30px] xl:text-[35px]`}>
            Our <span className="text-[#409F85]">Mission</span>{' '}
          </p>
          <p className={`mt-[42px] text-[16px] md:text-[20px]`}>
            The Population Project is a nonprofit organization, whose goal is to
            record the full name and place and date of birth of all living
            humans.
          </p>
          <br />
          <p className={`text-[16px] md:text-[20px]`}>
            Such an endeavor has never been attempted. Interestingly enough, in
            a time when information has never been so plentiful, we do not give
            much thought to the 8 billion people—individual people, each with
            their own name— who share this planet with us.
          </p>
        </div>
        {populationCount ? (
          <div
            className={`mt-[56px] flex flex-col items-center justify-center md:mt-[64px] xl:mt-0`}
          >
            <p
              style={{ lineHeight: '98px' }}
              className={`mb-[38px] text-[50px] font-semibold tabular-nums md:text-[80px]`}
            >
              {recordedPopulation}
            </p>
            <p
              style={{ lineHeight: '0.07em' }}
              className={`text-[20px] font-medium md:text-[25px]`}
            >
              HUMANS RECORDED
            </p>
          </div>
        ) : null}
      </div>
      <div className="relative flex w-full">
        <div
          ref={earthImageContainerRef}
          className={`relative z-[9] size-full max-md:hidden md:ml-[-160px] xl:ml-[-120px]`}
        >
          <Image
            priority={false}
            src="/images/what-earth-image.png"
            alt="Earth"
            width={100}
            height={100}
            onLoadingComplete={(result) => {
              if (result) {
                setTimeout(() => {
                  let newWidth;

                  if (earthImageContainerRef?.current) {
                    newWidth =
                      earthImageContainerRef?.current?.clientWidth / 3.5;
                  }

                  setEarthMarginRight(newWidth as number);
                }, 200);
              }
            }}
            layout="responsive"
            objectFit="contain"
          />
        </div>

        <div className="flex w-full flex-col md:ml-[-55px] md:pr-[10px] xl:ml-[-60px] xl:pr-[20px]">
          <p
            className={cn(
              'text-[25px] md:text-[30px] xl:text-[35px]',
              'md:mt-[-80px] xl:mt-[-10px]',
              'mb-[17px] xl:mb-[42px]',
              'pl-[30px] md:pl-0',
              'font-bold'
            )}
          >
            A Monumental <span className="text-[#409F85]">Task</span>{' '}
          </p>
          <p className="mb-[17px] text-[16px] max-md:pl-[30px] md:mb-[22px] xl:mb-[37px] xl:text-[20px]">
            Impossible? Perhaps. Consider some of the challenges:
          </p>
          <ul
            className={`pl-[45px] pr-[10px] text-[16px] md:pl-[35px] xl:text-[20px]`}
          >
            <li className="mb-[8px] flex xl:mb-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p>300,000 babies are born every day</p>
            </li>
            <li className="mb-[8px] flex xl:mb-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p>
                Not all births are registered, which makes tracking them
                difficult
              </p>
            </li>
            <li className="mb-[8px] flex xl:mb-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p>
                The information that we log is collected from multitudinous
                sources that must be cross-checked, again and again, for
                accuracy
              </p>
            </li>
            <li className="mb-[8px] flex xl:mb-[10px]">
              <p className="mr-[10px]">&#183;</p>
              <p>
                Sources we gather data from may contain errors; typos are known
                to happen even in electoral records and graduation lists
              </p>
            </li>
            <li className="flex">
              <p className="mr-[10px]">&#183;</p>
              <p>
                The world is in constant flux. Borders move. Cities are renamed.
                The only enduring certainty is change
              </p>
            </li>
          </ul>
          <div
            style={{
              paddingLeft: (earthMarginRight as number) * 2.5 + 'px',
            }}
            className={`absolute left-0 mt-[72px] hidden w-full bg-[#409F85] pr-[10px] md:bottom-[40px] md:flex md:py-[24px] md:pr-[20px] xl:bottom-[50px] xl:pr-[100px]`}
          >
            <p
              className={`text-[18px] text-[#ffffff] max-md:text-center md:text-[20px] xl:text-[25px]`}
            >
              For all these impediments, we’re doing our best to express the
              incredible strength and diversity of the human race.
            </p>
          </div>

          <div className="mt-[55px] bg-[#409F85] px-[30px] py-[38px] md:hidden">
            <p className="text-[18px] text-white">
              For all these impediments, we’re doing our best to express the
              incredible strength and diversity of the human race.
            </p>
          </div>
        </div>
      </div>
      <div
        className={`mt-[170px] flex flex-col items-center justify-center px-[25px] md:px-[55px] xl:px-[88px]`}
      >
        <div className={`relative flex h-[70px] w-full flex-col `}>
          <div
            ref={completionBarRef}
            className={`absolute h-[18px] w-full rounded-full bg-[#F1F1F1]`}
          />
          <div
            style={{ width: barWidth + 'px' }}
            className={`absolute h-[18px] rounded-full bg-[#5EAF99]`}
          />
          <div className="absolute flex h-[12px] w-full justify-between px-2 pt-[3px]">
            {dotsOfHistory(parseFloat(intPercentage))?.map((dot, index) => {
              return (
                <div className="relative" key={index}>
                  <div
                    className={`relative z-[4] size-[12px] ${
                      dot.color !== '#ffffff'
                        ? `bg-[${dot.color}]`
                        : 'cursor-pointer bg-white'
                    }  rounded-full`}
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-[12px]">
            <div
              style={{ marginLeft: barWidth + 'px' }}
              className="mt-[9px] flex h-[46px] items-end"
            >
              <div className="h-[46px] w-[2px] bg-[#409F85]" />
              <p className="ml-[9px] text-[25px]">{intPercentage} % Recorded</p>
            </div>
          </div>
        </div>
        <div className="mt-[42px] flex flex-col items-center justify-center">
          <p className="text-[25px] font-bold md:text-[30px] xl:text-[35px]">
            How You Can <span className="text-[#409F85]">Help</span>{' '}
          </p>
          <p className="mt-[37px] px-[35px] text-center md:px-[85px] xl:px-[332.5px]">
            The Population Project is more than a site, it is a worldwide
            initiative. There are many ways you can help from sharing us on
            social media to sending us lists of people.{' '}
          </p>
          <Link
            href="/how-you-can-help"
            onMouseEnter={handleReadMoreArrow}
            onMouseLeave={handleReadMoreArrow}
            className="mt-[53px] flex h-[45px] w-[221px] items-center justify-between rounded-[10px] border-2 border-[#409F85] bg-white px-[17px] text-[#409F85] transition hover:bg-[#409F85] hover:text-white xl:h-[60px]"
          >
            <p>READ MORE</p>
            <svg
              width="36"
              height="16"
              viewBox="0 0 36 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 7C0.447715 7 4.82823e-08 7.44772 0 8C-4.82823e-08 8.55228 0.447715 9 1 9L1 7ZM35.7071 8.70711C36.0976 8.31659 36.0976 7.68342 35.7071 7.2929L29.3431 0.928935C28.9526 0.53841 28.3195 0.53841 27.9289 0.928935C27.5384 1.31946 27.5384 1.95262 27.9289 2.34315L33.5858 8L27.9289 13.6569C27.5384 14.0474 27.5384 14.6805 27.9289 15.0711C28.3195 15.4616 28.9526 15.4616 29.3431 15.0711L35.7071 8.70711ZM1 9L35 9L35 7L1 7L1 9Z"
                fill={fillForReadMoreArrow}
                fillOpacity="0.87"
              />
            </svg>
          </Link>
        </div>
      </div>

      <NavFooter className="mt-[100px]" />
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const countries = await searchForAllCountriesStaticProps();

  return {
    props: {
      ...(await serverSideTranslations(locale || 'en', [
        'common',
        'about',
        'navbar',
        'new-homepage',
        'new-navbar',
        'new-footer',
        'what-is-the-population-project',
      ])),
      countries: countries || [],
    },
  };
};
